import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import clsx from 'clsx';

import _isEqual from 'lodash/isEqual';

import { LoadingSpinner, Panel } from 'reaxl';

import { dealsDuck, inventoryDuck, paymentsDuck, resultsDuck } from '@syndicated-wallet/ducks';
import { validateUDSConfig } from '@syndicated-wallet/helpers';

import {
    useShapeDealData,
    useSetWallet,
    useWalletManager,
    useWalletInitialization,
    useWalletId,
} from '@syndicated-wallet/hooks';

import { SEND_LEAD, OPEN_WALLET } from '@syndicated-wallet/constants/walletEvents';

import { UDS_PAYMENT_UPDATE_REQUEST, UDS_PAYMENT_UPDATE_COMPLETED } from '@syndicated-wallet/constants/udsEvents';

import { logger } from '@atc/syndicated-logging';

import { useDealerDeal } from 'wallet-deals';

import {
    getPriceLabel,
    getPriceDescription,
    UDS_CONFIG_COMPLETED,
    UDS_CONFIG_UPDATE_COMPLETED,
    useUDSUpdate,
} from 'wallet-manager';

import { eventBus } from '@atc/cai-event-bus';

import { ConnectedNativeDealCalculator } from 'reaxl-native-deal-calculator';
import trackUDSConfigTime from '@syndicated-wallet/helpers/trackUDSConfigTime';

logger.configure({ level: logger.logLevel.DEBUG });
const udsLogger = logger.getLogger('UDS');

/**
 * @module UnifiedDealStarter
 */

/**
 * UnifiedDealStarter component that integrates wallet functionalities with the Native Deal Calculator
 * This component handles configuration updates, payment information, and deal submission
 * It serves as the main integration point for wallet functionality in vehicle listing pages
 *
 * @returns {React.ReactElement} The rendered UnifiedDealStarter component
 */
export default function UnifiedDealStarter() {
    const ref = useRef();
    const dispatch = useDispatch();
    const paymentsInfo = useSelector(paymentsDuck.selectors.getPaymentsInfo);
    const isPaymentsInfoLoaded = useSelector(paymentsDuck.selectors.getIsPaymentsInfoLoaded);

    const [listingId, setListingId] = useState();
    const [listingVin, setListingVin] = useState();
    const [checkConfig, setCheckConfig] = useState(true);
    const [isWalletOpened, setIsWalletOpened] = useState(false);
    const [hasParentCssProps, setHasParentCssProps] = useState(false);

    // a ref to keep track of current paymentsInfo so we can avoid multiple updates
    // caused by other components
    const currentPaymentsInfo = useRef(paymentsInfo);
    const [vehiclePayments, setVehiclePayments] = useState({});
    const [config, setConfig] = useState({});
    // a ref to keep track of the current deal data for lead submission
    const dealToSubmit = useRef({});

    const checkForParentCssProps = async () => {
        const customElement = document.querySelector('unified-deal-starter');
        if (!customElement) {
            return false;
        }

        // loop through all parent elements until we find one with a transform
        for (let parent = customElement.parentElement; parent; parent = parent.parentElement) {
            if (parent.tagName && parent.tagName.toLowerCase() === 'iframe') {
                return false;
            }

            const { transform, filter, perspective } = window.getComputedStyle(parent);
            if (
                (transform && transform !== 'none') ||
                (filter && filter !== 'none') ||
                (perspective && perspective !== 'none')
            ) {
                return true;
            }
        }
        return false;
    };

    const updatePaymentsInfo = (data) => {
        // Not calling paymentsDuck.creators.updatePaymentsInfo action here because it throws an error:
        // A state mutation was detected inside a dispatch
        // And paymentsDuck.creators.updatePaymentsInfo action handles updating WalletStorage
        // which Wallet Modal should already handle as values are updated
        if (!_isEqual(data, currentPaymentsInfo.current)) {
            currentPaymentsInfo.current = { ...data };
            dispatch({
                type: paymentsDuck.types.UPDATE_PAYMENT_INFO,
                payload: { ...data },
            });
        }
    };

    const updateInventory = (listing) => {
        if (listing?.id) {
            setListingId(listing.id);
            setListingVin(listing?.vin);
            dispatch(inventoryDuck.creators.addInventory({ [listing.id]: { ...listing } }));
            dispatch(resultsDuck.creators.setActiveResults([listing.id]));
        }
    };

    const setExternalPayment = (applyResults, data) => {
        udsLogger.debug('Payment Update Request complete', data);
        if (data && Object.keys(data).length) {
            setVehiclePayments({ ...data, apply: applyResults });
        } else {
            setVehiclePayments({});
        }

        const urlParams = new URLSearchParams(window.location.search);
        const enablePerfDebug = urlParams.get('enablePerfDebug') === 'true';

        if (enablePerfDebug && window.performance && typeof window.performance.mark === 'function') {
            performance.mark('payment-rendered');
        }
    };

    const setConfigData = (configData = {}) => {
        if (checkConfig) {
            // Check for any required missing fields and console warn.
            validateUDSConfig(configData);
            setCheckConfig(false);
        }

        if (Object.keys(configData).length) {
            const { listing, isUDSStandAlone, ecommerce } = configData;
            setConfig(configData);
            if (listing) {
                updateInventory(listing);
            }

            // Track config time after initial config is set (listing presence implies config event complete)
            if (listing) {
                trackUDSConfigTime(ecommerce, listing?.vin, listing?.id);
            }

            if (isUDSStandAlone) {
                // Load wallet payments when UDS is standalone without wallet
                dispatch(paymentsDuck.creators.ensurePaymentsInfo());
            }
        } else {
            setConfig({});
            setListingId(null);
        }

        const urlParams = new URLSearchParams(window.location.search);
        const enablePerfDebug = urlParams.get('enablePerfDebug') === 'true';

        if (enablePerfDebug && window.performance && typeof window.performance.mark === 'function') {
            performance.mark('config-init');
        }
    };

    const setWalletOpened = () => {
        if (!isWalletOpened) {
            setIsWalletOpened(true);
        }
    };

    const onEmailCTAClick = () => {
        eventBus.emit(SEND_LEAD, dealToSubmit.current);
    };

    const onFetchPersonalizedPaymentDetails = (pencilingData = {}) => {
        udsLogger.debug('Payment Update Request', pencilingData);
        if (pencilingData?.userZip) {
            eventBus.emit(UDS_PAYMENT_UPDATE_REQUEST, pencilingData);
        }
    };

    // custom hook that shapes and updates dealToSubmit ref object for lead submission
    useShapeDealData({ dealToSubmit, listingId, config });

    useWalletInitialization({
        paymentsDuck,
        onComplete: updatePaymentsInfo,
    });

    useUDSUpdate({
        paymentsInfo: currentPaymentsInfo.current,
        config,
    });

    useDealerDeal({
        paymentsDuck,
        dealsDuck,
    });

    useEffect(() => {
        const detectParentCssProps = async () => {
            const result = await checkForParentCssProps();
            setHasParentCssProps(result);
        };
        detectParentCssProps();

        const setExternalPaymentListener = setExternalPayment.bind(null, true);
        const setConfigListener = setConfigData.bind();
        const setWalletOpenedListener = setWalletOpened.bind(null);

        eventBus.on(UDS_PAYMENT_UPDATE_COMPLETED, setExternalPaymentListener);
        eventBus.on(UDS_CONFIG_COMPLETED, setConfigListener);
        eventBus.on(UDS_CONFIG_UPDATE_COMPLETED, setConfigListener);
        eventBus.on(OPEN_WALLET, setWalletOpenedListener);

        return () => {
            eventBus.off(UDS_PAYMENT_UPDATE_COMPLETED, setExternalPaymentListener);
            eventBus.off(UDS_CONFIG_COMPLETED, setConfigListener);
            eventBus.off(UDS_CONFIG_UPDATE_COMPLETED, setConfigListener);
            eventBus.off(OPEN_WALLET, setWalletOpenedListener);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useWalletManager({
        currentPaymentsInfoRef: currentPaymentsInfo,
        paymentsDuck,
    });

    useSetWallet({
        isPaymentsInfoLoaded,
        paymentsInfo,
        prevPaymentsInfo: currentPaymentsInfo,
    });

    useWalletId({
        paymentsDuck,
    });

    /**
     * Determine price label based on vehicle make and type.
     * For new Toyota vehicles, use "TSRP" (Toyota Suggested Retail Price).
     * For all other vehicles, use the default "MSRP".
     */
    const priceLabel = useMemo(() => {
        const make = config?.listing?.make;
        const type = config?.listing?.type;
        const makeCode = make && typeof make === 'string' ? make.toUpperCase() : null;

        return getPriceLabel({ makeCode, type });
    }, [config?.listing?.make, config?.listing?.type]);

    const priceDescription = useMemo(() => {
        const make = config?.listing?.make;
        const type = config?.listing?.type;
        const makeCode = make && typeof make === 'string' ? make.toUpperCase() : null;

        return getPriceDescription({ makeCode, type });
    }, [config?.listing?.make, config?.listing?.type]);

    // props for NativeDealCalculator
    const calculatorProps = {
        config,
        container: ref?.current,
        dealsDuck,
        inventoryDuck,
        priceLabel,
        priceDescription,
        onEmailCTAClick,
        onFetchPersonalizedPaymentDetails,
        paymentsDuck,
        listingId,
        listingVin,
        vehiclePaymentDetails: vehiclePayments,
        enableNDSCalc: true,
        enableExernalFetchForVehicle: isWalletOpened,
    };

    // TODO: Analytics

    // TODO: any sub-modal components (Explore Financing) won't render properly in shadow dom if they don't have container prop set
    // example in syndicated-wallet https://ghe.coxautoinc.com/Autotrader/syndicated-wallet/blob/master/packages/syndicated-wallet/WalletModal/WalletModal.js#L52
    // passing "container" prop to Modal component defines where the modal will render

    // TODO: any popovers (Taxes & Fees) inside NDS should be either removed or find an alternative way to display (Popover doesn't work well in a webcomponent because of shadow dom)

    if (!listingId && config?.disableUDS === undefined) {
        return (
            <LoadingSpinner
                height={config?.isSingleColumn ? 870 : 661}
                className="display-flex align-items-center align-self-stretch"
            />
        );
    }

    if (config?.disableUDS) {
        udsLogger.debug('UDS is disabled from loading');
        return null;
    }

    return (
        !config?.disableUDS && (
            <div
                data-cmp="unifiedDealStarter"
                style={{ minHeight: config?.isSingleColumn ? '870px' : '661px' }}
                className={clsx({ 'modal-container': hasParentCssProps })}
            >
                {/** ref to container for any modal inside NDS **/}
                <div ref={ref} />
                <Panel>
                    <ConnectedNativeDealCalculator {...calculatorProps} />
                </Panel>
            </div>
        )
    );
}
